import { useEffect, useRef, useState, FocusEvent, KeyboardEvent } from "react";
import { Box, styled, SxProps, Typography as T, TypographyProps } from "@mui/material";

const StyledTypography = styled(T)(({ theme }) => ({
  padding: 4,
  borderRadius: 4,
  whiteSpace: "pre-wrap",
  display: "inline-block",
  position: "relative",
  transition: "background-color 0.3s ease",
  "&:hover:not(:focus)": {
    cursor: "pointer",
    backgroundColor: theme.palette.grey[200],
  },
  "&:focus-visible,:focus": {
    outline: `2px solid ${theme.palette.grey[200]}`,
  },
  "&:empty": {
    textAlign: "left",
    "&::before": {
      opacity: 0.5,
    },
  },
})) as typeof T;

interface EditableTextProps {
  handleBlur: (val: string) => void;
  loading?: boolean;
  maxWidth?: number;
  placeholder?: string;
  typographyProps?: TypographyProps;
  typographySx?: SxProps;
  value: string;
}

const EditableText = ({
  handleBlur,
  maxWidth,
  placeholder = "",
  typographyProps,
  typographySx,
  value,
}: EditableTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const editableRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isEditing) {
      editableRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <Box maxWidth={maxWidth}>
      <StyledTypography
        component="span"
        {...typographyProps}
        contentEditable={isEditing}
        suppressContentEditableWarning
        tabIndex={0}
        onClick={() => setIsEditing(true)}
        onBlur={(e: FocusEvent<HTMLSpanElement>) => {
          const val = e.currentTarget.textContent?.trim() ?? "";
          handleBlur(val);
          setIsEditing(false);
        }}
        onKeyDown={(e: KeyboardEvent<HTMLSpanElement>) => {
          // Handle enter press as blur/submit
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        sx={{
          "&:empty": {
            "&::before": {
              content: `"${placeholder}"`,
            },
          },
          ...typographySx,
        }}
      >
        {value}
      </StyledTypography>
    </Box>
  );
};

export default EditableText;
